import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Megaphone, Palette, Radio, Info, Sparkles, Copy, Image as ImageIcon, Video, Zap, Trash2, CalendarClock, Trophy, TrendingUp, Lightbulb,
} from "lucide-react";
import { sr } from "../../../lib/sr";
import { Head, Honest } from "./shared";

export function Creative() {
  const [plans, setPlans] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [learnings, setLearnings] = useState(null);
  const [channel, setChannel] = useState("Display");
  const [imgEngine, setImgEngine] = useState("gemini");
  const [videoAspect, setVideoAspect] = useState("16:9");
  const [busy, setBusy] = useState("");
  const [composer, setComposer] = useState(null);
  const [refine, setRefine] = useState(null);
  const [refineInput, setRefineInput] = useState("");
  const [varBusy, setVarBusy] = useState(null);
  const [selected, setSelected] = useState({});
  const [launch, setLaunch] = useState(null); // {items:[{creative_id, budget, format, platform}], total, mode}

  const toggleSelect = (c) => setSelected((s) => { const n = { ...s }; if (n[c.id]) delete n[c.id]; else n[c.id] = c; return n; });
  const makeVariations = async (c) => {
    setVarBusy(c.id);
    try { const r = await sr.creativeVariations(c.id, 3); r.ok ? toast.success(`Created ${r.count} variations`) : toast.error(r.note || "Couldn't create variations"); loadCreatives(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Variations failed"); }
    finally { setVarBusy(null); }
  };
  const openLaunch = () => launchCreatives(Object.values(selected));
  const launchCreatives = (list) => {
    if (!list.length) return;
    const isSoc = (c) => (c.channel || "").toLowerCase().includes("social");
    setLaunch({
      mode: "even", total: "",
      items: list.map((c) => ({ creative: c, budget: "", format: isSoc(c) ? "Reel" : null, platform: isSoc(c) ? "Instagram" : null })),
    });
  };
  const applyEven = (total, items) => {
    const t = parseFloat(total || "0") || 0;
    const per = items.length ? Math.round((t / items.length) * 100) / 100 : 0;
    return items.map((it) => ({ ...it, budget: t ? String(per) : "" }));
  };
  const submitLaunch = async () => {
    const items = launch.items.map((it) => ({
      creative_id: it.creative.id, budget_monthly: parseFloat(it.budget || "0") || 0,
      format: it.format || null, platform: it.platform || null,
    }));
    try {
      const r = await sr.launchBatch(items);
      toast.success(`Launched ${r.created} campaign(s) · $${Number(r.total_budget).toLocaleString()}/mo total`);
      setLaunch(null); setSelected({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't launch campaigns"); }
  };

  const [bulkRefine, setBulkRefine] = useState(null); // {text, busy}
  const [compare, setCompare] = useState(null); // {items, busy, root}
  const runBulkRefine = async () => {
    const ids = Object.keys(selected);
    setBulkRefine((b) => ({ ...b, busy: true }));
    try {
      const r = await sr.refineBulk(ids, bulkRefine.text);
      toast.success(`Refined ${r.refined} creative(s)${r.failed ? ` · ${r.failed} skipped` : ""}`);
      setBulkRefine(null); setSelected({}); loadCreatives();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk refine failed"); setBulkRefine((b) => ({ ...b, busy: false })); }
  };
  const openCompare = async (c) => {
    setCompare({ items: [], busy: true });
    try {
      const r = await sr.creativeFamily(c.id);
      setCompare({ items: r.items || [], busy: false, root: r.root });
      if ((r.items || []).length > 1) {
        try {
          const s = await sr.suggestWinner(c.id);
          if (s.ok) setCompare((cp) => cp ? ({ ...cp, suggestedId: s.suggested_id, suggestReason: s.reason, suggestInsight: s.insight, suggestLabel: s.label, suggestDisclaimer: s.disclaimer, scores: s.scores || [] }) : cp);
        } catch { /* suggestion is best-effort */ }
      }
    }
    catch { toast.error("Couldn't load variations"); setCompare(null); }
  };
  const chooseWinner = async (id) => {
    try { await sr.pickWinner(id); setCompare((cp) => ({ ...cp, items: cp.items.map((x) => ({ ...x, winner: x.id === id })) })); loadCreatives(); toast.success("Winner picked"); }
    catch { toast.error("Couldn't pick winner"); }
  };
  const launchFromCompare = (c) => { setCompare(null); launchCreatives([c]); };
  const acceptAndLaunch = async (id) => {
    const winner = (compare?.items || []).find((x) => x.id === id);
    if (!winner) return;
    try { await sr.pickWinner(id); loadCreatives(); } catch { /* still allow launch */ }
    setCompare(null);
    launchCreatives([winner]);
  };
  const [presetBusy, setPresetBusy] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetModal, setPresetModal] = useState(null); // {text, busy}
  const loadPresets = () => sr.listRefinePresets().then((r) => setPresets(r.presets || [])).catch(() => {});
  const savePreset = async () => {
    const text = (presetModal?.text || "").trim();
    if (!text) return;
    setPresetModal((m) => ({ ...m, busy: true }));
    try { await sr.addRefinePreset(text); toast.success("Preset saved"); setPresetModal(null); loadPresets(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save preset"); setPresetModal((m) => ({ ...m, busy: false })); }
  };
  const removePreset = async (id) => {
    try { await sr.deleteRefinePreset(id); loadPresets(); }
    catch { toast.error("Couldn't remove preset"); }
  };
  const applyPreset = async (c, text) => {
    setPresetBusy(c.id);
    try { const r = await sr.refineCreative(c.id, text); r.ok !== false ? toast.success("Refined") : toast.error(r.reply || "Couldn't refine"); loadCreatives(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refine failed"); }
    finally { setPresetBusy(null); }
  };
  const runBulkVariations = async () => {
    const ids = Object.keys(selected);
    try { const r = await sr.variationsBulk(ids, 3); toast.success(`Created ${r.created} variations across ${ids.length} creatives`); setSelected({}); loadCreatives(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk variations failed"); }
  };

  const sendRefine = async () => {
    const text = refineInput.trim();
    if (!text || !refine) return;
    setRefine((r) => ({ ...r, busy: true }));
    setRefineInput("");
    try {
      const res = await sr.refineCreative(refine.creative.id, text);
      if (res.ok) {
        setCreatives((list) => list.map((x) => (x.id === res.creative.id ? res.creative : x)));
        setRefine({ creative: res.creative, busy: false });
      } else {
        setRefine((r) => ({ ...r, busy: false, creative: { ...r.creative, chat: [...(r.creative.chat || []), { role: "user", text }, { role: "assistant", text: res.reply || "Couldn't apply that." }] } }));
      }
    } catch (e) {
      setRefine((r) => ({ ...r, busy: false }));
      toast.error(e?.response?.data?.detail || "Refine failed");
    }
  };

  const loadLearnings = () => sr.creativeLearnings().then(setLearnings).catch(() => {});
  const loadCreatives = () => sr.listCreatives().then((r) => {
    const rows = r.creatives || [];
    setCreatives(rows);
    loadLearnings();
    rows.forEach((c) => { if (c.type === "video" && !c.video_url && ["pending", "generating"].includes(c.status)) pollVideoStatus(c.id, 0); });
  }).catch(() => {});

  const attachCreative = async (creative, target) => {
    try {
      const r = await sr.useCreativeInAd({ creative_id: creative.id, target });
      toast.success(`Added to campaign draft "${r.draft_name || "latest"}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't attach this creative"); }
  };

  const submitPost = async () => {
    const { creative, message, when, now } = composer;
    try {
      await sr.useCreativeInAd({ creative_id: creative.id, target: "post", message: message || undefined,
        publish_now: now || undefined, scheduled_at: (!now && when) ? new Date(when).toISOString() : undefined });
      toast.success(now ? "Published to Facebook" : "Scheduled as a Facebook post");
      setComposer(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post"); }
  };

  const pollVideoStatus = async (id, tries) => {
    if (tries > 30) return;
    try {
      const s = await sr.adVideoStatus(id);
      if (s.status === "completed") { toast.success("AI video ready"); loadCreatives(); return; }
      if (s.status === "failed") { toast.error("AI video failed to generate"); return; }
    } catch { /* keep polling */ }
    setTimeout(() => pollVideoStatus(id, tries + 1), 10000);
  };
  useEffect(() => {
    sr.listPlans().then((r) => setPlans(r.growth_plans)).catch(() => setPlans([]));
    loadCreatives();
    loadPresets();
  }, []);

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "copy") { const r = await sr.generateCreative({ channel, count: 3 }); toast.success(r.ai_available ? "AI created 3 ad concepts" : "Draft concepts created"); }
      else if (kind === "image") { const r = await sr.generateImage({ channel, engine: imgEngine }); r.ok ? toast.success(`AI banner image created (${imgEngine === "openai" ? "GPT Image" : "Nano Banana"})`) : toast.error(r.note || "Image unavailable"); }
      else if (kind === "video") { const r = await sr.generateVideoScript({ duration_sec: 15 }); toast.success(r.ai_available ? "AI video script created" : "Draft script created"); }
      else if (kind === "video_ai") { const r = await sr.generateAdVideo({ aspect_ratio: videoAspect }); if (!r.ok) { toast.error(r.note || "AI video not enabled yet"); } else { toast.success("AI video generating — this can take a minute or two"); pollVideoStatus(r.creative.id, 0); } }
      loadCreatives();
    } catch { toast.error("Generation failed"); } finally { setBusy(""); }
  };

  if (!plans) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const planConcepts = plans.flatMap((p) => (p.plan?.creative_set || []).map((c) => ({ c, goal: p.goal })));
  const selCount = Object.keys(selected).length;
  const familyCount = {};
  creatives.forEach((c) => { const root = c.variation_of || c.id; familyCount[root] = (familyCount[root] || 0) + 1; });
  const famSize = (c) => familyCount[c.variation_of || c.id] || 1;
  const launchFooter = (c, { showVar = true } = {}) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", color: "var(--navy)" }} data-testid={`select-label-${c.id}`}>
        <input type="checkbox" checked={!!selected[c.id]} onChange={() => toggleSelect(c)} data-testid={`select-${c.id}`} /> Select
      </label>
      {c.winner && <span className="sr-badge sr-badge-green" data-testid={`winner-${c.id}`}><Trophy size={11} /> Winner</span>}
      {famSize(c) > 1 && (
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => openCompare(c)} data-testid={`compare-${c.id}`}>
          <Copy size={13} /> Compare ({famSize(c)})
        </button>
      )}
      {showVar && (
        <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginLeft: "auto" }} onClick={() => makeVariations(c)} disabled={varBusy === c.id} data-testid={`variations-${c.id}`}>
          {varBusy === c.id ? <Loader2 size={13} className="sr-spin" /> : <Copy size={13} />} Create variations
        </button>
      )}
      {c.type !== "image" && c.type !== "video" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", width: "100%", marginTop: 2 }}>
          {["shorter", "more urgent", "friendlier"].map((p) => (
            <button key={p} className="sr-chip" onClick={() => applyPreset(c, `Make it ${p}`)} disabled={presetBusy === c.id} data-testid={`preset-${p.split(" ").join("-")}-${c.id}`}
              style={{ fontSize: 11, padding: "3px 9px", borderRadius: 999, border: "1px solid var(--border)", background: "#fff", color: "var(--navy)", cursor: "pointer", textTransform: "capitalize" }}>
              {presetBusy === c.id ? "…" : p}
            </button>
          ))}
          {presets.map((pr) => (
            <span key={pr.id} className="sr-chip" data-testid={`custom-preset-${pr.id}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, padding: "3px 6px 3px 9px", borderRadius: 999, border: "1px solid var(--gold)", background: "#FFFBF0", color: "var(--navy)" }}>
              <button onClick={() => applyPreset(c, pr.text)} disabled={presetBusy === c.id} title={pr.text}
                style={{ border: "none", background: "transparent", color: "var(--navy)", cursor: "pointer", padding: 0, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {pr.text}
              </button>
              <button onClick={() => removePreset(pr.id)} title="Remove preset" data-testid={`custom-preset-del-${pr.id}`}
                style={{ border: "none", background: "transparent", color: "var(--muted)", cursor: "pointer", padding: 0, lineHeight: 1, display: "inline-flex" }}>
                <Trash2 size={10} />
              </button>
            </span>
          ))}
          <button className="sr-chip" onClick={() => setPresetModal({ text: "", busy: false })} data-testid="add-preset-chip"
            style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, padding: "3px 9px", borderRadius: 999, border: "1px dashed var(--border)", background: "#fff", color: "var(--blue)", cursor: "pointer" }}>
            <Plus size={11} /> Save prompt
          </button>
        </div>
      )}
    </div>
  );


  return (
    <div>
      <Head title="Creative" sub="AI-generated ad copy, banner images and video scripts — plus concepts from your growth plan." />

      <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
          <Sparkles size={20} color="var(--blue)" />
          <strong style={{ color: "var(--navy)" }}>AI Ad Creation</strong>
          <span className="sr-badge">Creative Director agent</span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ minWidth: 160 }}>
            <label className="sr-label">Channel</label>
            <select className="sr-select" value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="creative-channel">
              {["Display", "Native", "Video", "Audio", "Radio", "Email", "SMS"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <label className="sr-label">Image engine</label>
            <select className="sr-select" value={imgEngine} onChange={(e) => setImgEngine(e.target.value)} data-testid="creative-image-engine">
              <option value="gemini">Gemini Nano Banana</option>
              <option value="openai">OpenAI GPT Image 1</option>
            </select>
          </div>
          <button className="sr-btn sr-btn-primary" onClick={() => run("copy")} disabled={!!busy} data-testid="creative-generate-btn">
            {busy === "copy" ? <Loader2 size={15} className="sr-spin" /> : <Sparkles size={15} />} Ad copy
          </button>
          <button className="sr-btn sr-btn-gold" onClick={() => run("image")} disabled={!!busy} data-testid="creative-image-btn">
            {busy === "image" ? <Loader2 size={15} className="sr-spin" /> : <ImageIcon size={15} />} Banner image
          </button>
          <button className="sr-btn sr-btn-ghost" onClick={() => run("video")} disabled={!!busy} data-testid="creative-video-btn">
            {busy === "video" ? <Loader2 size={15} className="sr-spin" /> : <Video size={15} />} Video script
          </button>
          <div style={{ minWidth: 130 }}>
            <label className="sr-label">Video format</label>
            <select className="sr-select" value={videoAspect} onChange={(e) => setVideoAspect(e.target.value)} data-testid="creative-video-aspect">
              <option value="16:9">Wide — Feed (16:9)</option>
              <option value="9:16">Vertical — Reels/Stories (9:16)</option>
              <option value="1:1">Square — Feed (1:1)</option>
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }} title={`Preview: ${videoAspect}`}>
            <label className="sr-label" style={{ visibility: "hidden" }}>fmt</label>
            {(() => {
              const dims = { "16:9": { w: 42, h: 24 }, "9:16": { w: 24, h: 42 }, "1:1": { w: 32, h: 32 } }[videoAspect] || { w: 42, h: 24 };
              return <div data-testid="video-format-preview" style={{ width: dims.w, height: dims.h, border: "2px solid var(--blue)", borderRadius: 4, background: "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "var(--blue)", fontWeight: 700 }}>{videoAspect}</div>;
            })()}
          </div>
          <button className="sr-btn sr-btn-ghost" onClick={() => run("video_ai")} disabled={!!busy} data-testid="creative-video-ai-btn">
            {busy === "video_ai" ? <Loader2 size={15} className="sr-spin" /> : <Video size={15} />} AI video
          </button>
        </div>
      </div>

      {learnings && learnings.total > 0 && (
        <div className="sr-card" style={{ marginTop: 16 }} data-testid="creative-learnings">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <TrendingUp size={18} style={{ color: "var(--blue)" }} />
            <strong style={{ color: "var(--navy)" }}>What's working</strong>
            <span className="sr-badge sr-badge-gray">Creative learning</span>
            <span className="sr-badge" style={{ marginLeft: "auto", color: "#067647", background: "#F1FBF4", border: "1px solid #B6E3C6" }} data-testid="learnings-win-rate">
              <Trophy size={11} /> {learnings.winners} winner{learnings.winners === 1 ? "" : "s"} · {learnings.win_rate}% win rate
            </span>
          </div>
          {learnings.winners === 0 && (
            <div data-testid="learnings-nudge" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 12, borderRadius: 10, background: "#FFF8E6", border: "1px solid #F2D98A", fontSize: 13, color: "#7A5410" }}>
              <Trophy size={16} style={{ color: "#B8791B", flexShrink: 0 }} />
              <span>Pick a winner on any A/B test to start learning — we'll turn your best-performing ads into repeatable patterns right here.</span>
            </div>
          )}
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
            {[["Top channels", learnings.top_channels], ["Best CTAs", learnings.top_ctas], ["Top angles", learnings.top_angles]].map(([label, list]) => (
              <div key={label} className="sr-card" style={{ padding: 12, background: "#F7FAFF", border: "1px solid #DCE6F5" }} data-testid={`learnings-group-${label.split(" ").join("-").toLowerCase()}`}>
                <div className="sr-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
                {(list || []).length === 0 ? <p className="sr-muted" style={{ fontSize: 12, margin: 0 }}>Not enough data yet.</p> : (
                  <div style={{ display: "grid", gap: 6 }}>
                    {(list || []).slice(0, 4).map((row, i) => (
                      <div key={row.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--navy)" }}>
                        <span style={{ fontWeight: 700, color: i === 0 ? "var(--blue)" : "var(--navy)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.key}</span>
                        {row.wins > 0 && <span className="sr-badge sr-badge-green" style={{ fontSize: 10 }}><Trophy size={9} /> {row.wins}</span>}
                        <span className="sr-muted" style={{ fontSize: 11 }}>{row.avg_score}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {(learnings.learnings || []).length > 0 && (
            <div style={{ marginTop: 12, display: "grid", gap: 6 }} data-testid="learnings-takeaways">
              {learnings.learnings.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "var(--navy)" }}>
                  <Lightbulb size={14} style={{ color: "#B8791B", flexShrink: 0, marginTop: 2 }} /> {t}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {creatives.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h2 style={{ fontSize: 18 }}>AI-created</h2>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", marginTop: 12 }}>
            {creatives.map((c) => {
              if (c.type === "image") return (
                <div key={c.id} className="sr-card sr-card-hover" data-testid={`ai-image-${c.id}`}>
                  <img src={c.image_data} alt="AI banner" style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)" }} />
                  <span className="sr-badge sr-badge-gray" style={{ marginTop: 10 }}>{c.channel} · AI image{c.engine ? ` · ${c.engine === "openai" ? "GPT Image" : "Nano Banana"}` : ""}</span>
                  <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                    <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ flex: 1 }} onClick={() => attachCreative(c, "campaign")} data-testid={`use-campaign-${c.id}`}><Megaphone size={13} /> Add to campaign</button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ flex: 1 }} onClick={() => setComposer({ creative: c, message: "", when: "" })} data-testid={`use-post-${c.id}`}><CalendarClock size={13} /> Schedule post</button>
                  </div>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 8, width: "100%" }} onClick={() => setRefine({ creative: c, busy: false })} data-testid={`refine-${c.id}`}><Sparkles size={13} /> Refine with AI</button>
                  {launchFooter(c)}
                </div>
              );
              if (c.type === "video") {
                if (c.video_url) return (
                  <div key={c.id} className="sr-card sr-card-hover" data-testid={`ai-video-clip-${c.id}`}>
                    <video src={c.video_url} controls playsInline style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)", background: "#000" }} />
                    <span className="sr-badge sr-badge-gray" style={{ marginTop: 10 }}>AI video</span>
                    <div style={{ marginTop: 10 }}>
                      <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ width: "100%" }} onClick={() => attachCreative(c, "campaign")} data-testid={`use-campaign-${c.id}`}><Megaphone size={13} /> Add to campaign</button>
                    </div>
                    {launchFooter(c, { showVar: false })}
                  </div>
                );
                if (["pending", "generating"].includes(c.status)) return (
                  <div key={c.id} className="sr-card" data-testid={`ai-video-generating-${c.id}`} style={{ textAlign: "center" }}>
                    <div style={{ padding: "26px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                      <Loader2 size={28} className="sr-spin" style={{ color: "var(--blue)" }} />
                      <div style={{ fontWeight: 700, color: "var(--navy)" }}>Generating AI video…</div>
                      <div className="sr-muted" style={{ fontSize: 12, maxWidth: 220 }}>Usually 1–2 minutes. It will appear here automatically when ready.</div>
                    </div>
                    <span className="sr-badge sr-badge-amber">Rendering</span>
                  </div>
                );
                return null;
              }
              if (c.type === "video_script") { const s = c.script || {}; const txt = (v) => (v == null ? "" : typeof v === "string" ? v : (v.on_screen || v.voiceover || v.visual || "")); return (
                <div key={c.id} className="sr-card sr-card-hover" data-testid={`ai-video-${c.id}`}>
                  <span className="sr-badge">{s.duration_sec || 15}s video</span>
                  <h3 style={{ fontSize: 16, marginTop: 8 }}>{txt(s.title)}</h3>
                  <ol style={{ paddingLeft: 18, fontSize: 13, marginTop: 8 }}>
                    {(s.scenes || []).map((sc, i) => <li key={i} style={{ marginBottom: 6 }}><strong>{txt(sc.on_screen) || "Scene"}</strong> — {txt(sc.voiceover)}</li>)}
                  </ol>
                  <span className="sr-badge sr-badge-gold">{txt(s.cta)}</span>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 10, width: "100%" }} onClick={() => setRefine({ creative: c, busy: false })} data-testid={`refine-${c.id}`}><Sparkles size={13} /> Refine with AI</button>
                  {launchFooter(c)}
                </div>
              ); }
              return (
                <div key={c.id} className="sr-card sr-card-hover" data-testid={`ai-creative-${c.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="sr-badge sr-badge-gray">{c.channel}</span>{c.angle && <span className="sr-badge">{c.angle}</span>}
                  </div>
                  <h3 style={{ fontSize: 17, marginTop: 10 }}>{c.headline}</h3>
                  <p className="sr-muted" style={{ fontSize: 14, marginTop: 6 }}>{c.body}</p>
                  <span className="sr-badge sr-badge-gold" style={{ marginTop: 10 }}>{c.cta}</span>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 10, width: "100%" }} onClick={() => setRefine({ creative: c, busy: false })} data-testid={`refine-${c.id}`}><Sparkles size={13} /> Refine with AI</button>
                  {launchFooter(c)}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <h2 style={{ fontSize: 18, marginTop: 28 }}>From your growth plan</h2>
      {planConcepts.length === 0 ? (
        <Honest icon={Palette} title="No plan concepts yet" body="Generate a growth plan and its creative concepts will appear here for production." />
      ) : (
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", marginTop: 12 }}>
          {planConcepts.map((s, i) => (
            <div key={i} className="sr-card sr-card-hover" data-testid={`creative-${i}`}>
              <span className="sr-badge sr-badge-gray" style={{ textTransform: "capitalize" }}>{s.goal}</span>
              <p style={{ marginTop: 10, color: "var(--navy)", fontWeight: 600 }}>{s.c}</p>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Concept · ready for production & approval</p>
            </div>
          ))}
        </div>
      )}

      {composer && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setComposer(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="post-composer">
          <div className="sr-card" style={{ maxWidth: 520, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ fontSize: 18, marginBottom: 4 }}>Schedule as Facebook post</h3>
            <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>Write a caption, then post now or pick a time.</p>
            {composer.creative.image_data && <img src={composer.creative.image_data} alt="creative" style={{ width: "100%", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 12 }} />}
            <label className="sr-label">Caption</label>
            <textarea className="sr-input" style={{ minHeight: 96, resize: "vertical" }} value={composer.message} onChange={(e) => setComposer({ ...composer, message: e.target.value })} placeholder="Write your post caption…" data-testid="composer-caption" />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={!!composer.now} onChange={(e) => setComposer({ ...composer, now: e.target.checked })} data-testid="composer-publish-now" />
              Publish immediately
            </label>
            {!composer.now && (<>
              <label className="sr-label" style={{ marginTop: 10 }}>Publish time (optional — defaults to tomorrow)</label>
              <input className="sr-input" type="datetime-local" value={composer.when} onChange={(e) => setComposer({ ...composer, when: e.target.value })} data-testid="composer-when" />
            </>)}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button className="sr-btn sr-btn-ghost" onClick={() => setComposer(null)} data-testid="composer-cancel">Cancel</button>
              <button className="sr-btn sr-btn-primary" onClick={submitPost} data-testid="composer-submit"><CalendarClock size={15} /> {composer.now ? "Publish now" : "Schedule post"}</button>
            </div>
          </div>
        </div>
      )}

      {refine && (
        <div onClick={(e) => { if (e.target === e.currentTarget) { setRefine(null); setRefineInput(""); } }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="refine-modal">
          <div className="sr-card" style={{ maxWidth: 520, width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ fontSize: 17 }}><Sparkles size={16} style={{ verticalAlign: "-2px", color: "var(--gold)" }} /> Refine with AI</h3>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setRefine(null); setRefineInput(""); }} data-testid="refine-close">Close</button>
            </div>
            {refine.creative.type === "image" && refine.creative.image_data &&
              <img src={refine.creative.image_data} alt="creative" style={{ width: "100%", borderRadius: 8, border: "1px solid var(--border)", marginBottom: 8 }} />}
            {(refine.creative.headline || refine.creative.body) &&
              <div style={{ background: "#F7FAFF", border: "1px solid var(--border)", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: "var(--navy)" }}>{refine.creative.headline}</div>
                <div className="sr-muted" style={{ fontSize: 13 }}>{refine.creative.body}</div>
              </div>}
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, minHeight: 80, marginBottom: 10 }} data-testid="refine-thread">
              {(refine.creative.chat || []).length === 0 && <p className="sr-muted" style={{ fontSize: 13 }}>Tell me what to change — e.g. “make it shorter and add urgency”, “friendlier tone”, or (for images) “brighter, show a coffee cup”.</p>}
              {(refine.creative.chat || []).map((m, i) => (
                <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%", background: m.role === "user" ? "var(--navy)" : "#EEF3FA", color: m.role === "user" ? "#fff" : "var(--navy)", borderRadius: 10, padding: "8px 10px", fontSize: 13 }}>
                  {m.text}
                </div>
              ))}
              {refine.busy && <div style={{ alignSelf: "flex-start", color: "var(--blue)", fontSize: 13 }}><Loader2 size={14} className="sr-spin" /> Applying…</div>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="sr-input" style={{ flex: 1 }} value={refineInput} onChange={(e) => setRefineInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !refine.busy) sendRefine(); }}
                placeholder="Describe the change…" data-testid="refine-input" disabled={refine.busy} />
              <button className="sr-btn sr-btn-primary" onClick={sendRefine} disabled={refine.busy || !refineInput.trim()} data-testid="refine-send">Send</button>
            </div>
          </div>
        </div>
      )}

      {selCount > 0 && !launch && (
        <div data-testid="launch-bar" style={{ position: "fixed", left: "50%", bottom: 20, transform: "translateX(-50%)", zIndex: 900,
          background: "var(--navy)", color: "#fff", borderRadius: 999, padding: "10px 16px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 8px 24px rgba(11,36,71,0.3)" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{selCount} selected</span>
          <button className="sr-btn sr-btn-sm" style={{ background: "#fff", color: "var(--navy)" }} onClick={openLaunch} data-testid="launch-open"><Megaphone size={14} /> Launch to campaigns</button>
          <button className="sr-btn sr-btn-sm" style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)" }} onClick={() => setBulkRefine({ text: "", busy: false })} data-testid="bulk-refine-open"><Sparkles size={14} /> Refine all with AI</button>
          <button className="sr-btn sr-btn-sm" style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)" }} onClick={runBulkVariations} data-testid="bulk-variations-open"><Copy size={14} /> Variations for all</button>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ color: "#fff" }} onClick={() => setSelected({})} data-testid="launch-clear">Clear</button>
        </div>
      )}

      {launch && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setLaunch(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="launch-modal">
          <div className="sr-card" style={{ maxWidth: 620, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h3 style={{ fontSize: 18 }}><Megaphone size={16} style={{ verticalAlign: "-2px", color: "var(--blue)" }} /> Launch {launch.items.length} to campaigns</h3>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setLaunch(null)} data-testid="launch-modal-close">Close</button>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {[["even", "Split evenly"], ["individual", "Individual budgets"]].map(([k, label]) => (
                  <button key={k} onClick={() => setLaunch((l) => ({ ...l, mode: k, items: k === "even" ? applyEven(l.total, l.items) : l.items }))}
                    data-testid={`budget-mode-${k}`}
                    style={{ border: "1px solid " + (launch.mode === k ? "var(--blue)" : "var(--border)"), borderRadius: 999,
                      background: launch.mode === k ? "var(--blue)" : "#fff", color: launch.mode === k ? "#fff" : "var(--navy)", fontSize: 12, fontWeight: 700, padding: "6px 12px", cursor: "pointer" }}>{label}</button>
                ))}
              </div>
              {launch.mode === "even" && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                  <span className="sr-muted" style={{ fontSize: 13 }}>Total $</span>
                  <input className="sr-input" type="number" min="0" placeholder="Total monthly" value={launch.total} data-testid="launch-total"
                    onChange={(e) => setLaunch((l) => ({ ...l, total: e.target.value, items: applyEven(e.target.value, l.items) }))} style={{ maxWidth: 140 }} />
                  <span className="sr-muted" style={{ fontSize: 13 }}>/mo</span>
                </div>
              )}
              {launch.mode === "individual" && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                  <span className="sr-muted" style={{ fontSize: 13 }}>Total</span>
                  <strong style={{ color: "var(--navy)", fontSize: 14 }} data-testid="launch-total">
                    ${launch.items.reduce((s, it) => s + (parseFloat(it.budget || "0") || 0), 0).toLocaleString()}
                  </strong>
                  <span className="sr-muted" style={{ fontSize: 13 }}>/mo</span>
                </div>
              )}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {launch.items.map((it, idx) => {
                const isSoc = (it.creative.channel || "").toLowerCase().includes("social");
                const setItem = (patch) => setLaunch((l) => ({ ...l, items: l.items.map((x, i) => (i === idx ? { ...x, ...patch } : x)) }));
                return (
                  <div key={it.creative.id} className="sr-card" style={{ padding: 12 }} data-testid={`launch-item-${it.creative.id}`}>
                    <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>{it.creative.headline || it.creative.angle || it.creative.channel || "Creative"}</div>
                    <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }}>{it.creative.channel || "Display"}</div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span className="sr-muted" style={{ fontSize: 13 }}>$</span>
                        <input className="sr-input" type="number" min="0" value={it.budget} data-testid={`launch-budget-${it.creative.id}`}
                          disabled={launch.mode === "even"} onChange={(e) => setItem({ budget: e.target.value })} style={{ maxWidth: 110 }} placeholder="Budget" />
                        <span className="sr-muted" style={{ fontSize: 13 }}>/mo</span>
                      </div>
                      {isSoc && (
                        <>
                          <select className="sr-select" value={it.format || "Reel"} onChange={(e) => setItem({ format: e.target.value })} data-testid={`launch-format-${it.creative.id}`} style={{ fontSize: 13 }}>
                            <option>Reel</option>
                            <option>Promoted Post</option>
                          </select>
                          <select className="sr-select" value={it.platform || "Instagram"} onChange={(e) => setItem({ platform: e.target.value })} data-testid={`launch-platform-${it.creative.id}`} style={{ fontSize: 13 }}>
                            {["Instagram", "Facebook", "TikTok", "YouTube Shorts"].map((p) => <option key={p}>{p}</option>)}
                          </select>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button className="sr-btn sr-btn-primary" style={{ width: "100%", marginTop: 14 }} onClick={submitLaunch} data-testid="launch-submit">
              <Megaphone size={15} /> Launch {launch.items.length} campaign{launch.items.length > 1 ? "s" : ""}
            </button>
            {launch.items.some((it) => { const b = parseFloat(it.budget || "0") || 0; return b > 0 && b < 150; }) && (
              <div data-testid="budget-warning" style={{ marginTop: 8, fontSize: 12, color: "#B8791B" }}>
                <p style={{ display: "flex", gap: 6, alignItems: "flex-start", margin: 0 }}>
                  <Info size={13} style={{ marginTop: 1, flexShrink: 0 }} /> Some campaigns are under about $5/day ($150/mo) — spend that low often struggles to deliver.
                </p>
                <button className="sr-btn sr-btn-sm" style={{ marginTop: 6 }} data-testid="guardrail-autofix"
                  onClick={() => setLaunch((l) => {
                    const items = l.items.map((it) => { const b = parseFloat(it.budget || "0") || 0; return b < 150 ? { ...it, budget: "150" } : it; });
                    const total = items.reduce((sum, it) => sum + (parseFloat(it.budget || "0") || 0), 0);
                    return { ...l, mode: "individual", items, total: String(total) };
                  })}>
                  <Zap size={13} /> Top up each to $150/mo
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {bulkRefine && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setBulkRefine(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="bulk-refine-modal">
          <div className="sr-card" style={{ maxWidth: 480, width: "100%" }}>
            <h3 style={{ fontSize: 18, marginBottom: 4 }}><Sparkles size={16} style={{ verticalAlign: "-2px", color: "var(--blue)" }} /> Refine {selCount} selected</h3>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 0 }}>One instruction, applied to every selected creative — e.g. "make it warmer and add urgency".</p>
            <textarea className="sr-input" rows={3} value={bulkRefine.text} onChange={(e) => setBulkRefine((b) => ({ ...b, text: e.target.value }))}
              placeholder="Describe the change for all selected…" data-testid="bulk-refine-input" style={{ width: "100%", marginTop: 8 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setBulkRefine(null)} data-testid="bulk-refine-cancel">Cancel</button>
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={runBulkRefine} disabled={bulkRefine.busy || !bulkRefine.text.trim()} data-testid="bulk-refine-send">
                {bulkRefine.busy ? <Loader2 size={14} className="sr-spin" /> : <Sparkles size={14} />} Refine all
              </button>
            </div>
          </div>
        </div>
      )}

      {compare && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setCompare(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="compare-modal">
          <div className="sr-card" style={{ maxWidth: 820, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ fontSize: 18 }}><Copy size={16} style={{ verticalAlign: "-2px", color: "var(--blue)" }} /> Compare variations · pick the winner</h3>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setCompare(null)} data-testid="compare-close">Close</button>
            </div>
            {!compare.busy && compare.suggestedId && !compare.items.some((x) => x.winner) && (
              <div data-testid="winner-suggestion" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#FFF8E6", border: "1px solid #F2D98A", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                <Sparkles size={16} style={{ color: "#B8791B", flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: "var(--navy)", flex: 1, minWidth: 180 }} data-testid="winner-insight">
                  <strong>{compare.suggestLabel || "Creative quality suggestion"}:</strong> {compare.suggestInsight || compare.suggestReason}
                  {compare.suggestDisclaimer && <span className="sr-muted" style={{ display: "block", fontSize: 11 }}>{compare.suggestDisclaimer}</span>}
                </span>
                <button className="sr-btn sr-btn-sm" onClick={() => chooseWinner(compare.suggestedId)} data-testid="accept-suggestion"
                  style={{ border: "1px solid var(--gold)", background: "#fff", color: "var(--navy)" }}>
                  <Trophy size={13} /> Accept
                </button>
                <button className="sr-btn sr-btn-sm sr-btn-primary" onClick={() => acceptAndLaunch(compare.suggestedId)} data-testid="accept-and-launch">
                  <Megaphone size={13} /> Accept &amp; Launch
                </button>
              </div>
            )}
            {compare.busy ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
                {compare.items.map((c) => {
                  const isSuggested = compare.suggestedId === c.id;
                  const sc = (compare.scores || []).find((s) => s.id === c.id);
                  return (
                  <div key={c.id} className="sr-card" style={{ border: c.winner ? "2px solid #067647" : (isSuggested ? "2px solid #E0A93B" : "1px solid var(--border)"), position: "relative" }} data-testid={`compare-item-${c.id}`}>
                    {isSuggested && !c.winner && (
                      <span className="sr-badge" style={{ position: "absolute", top: 8, right: 8, background: "#FFF3D6", color: "#B8791B", border: "1px solid #F2D98A" }} data-testid={`auto-picked-${c.id}`}>
                        <Sparkles size={11} /> Auto-picked
                      </span>
                    )}
                    {c.type === "image" && c.image_data ? <img src={c.image_data} alt="" style={{ width: "100%", borderRadius: 8 }} /> : (
                      <>
                        <span className="sr-badge sr-badge-gray">{c.channel || "Copy"}</span>
                        <h4 style={{ fontSize: 15, marginTop: 8 }}>{c.headline}</h4>
                        <p className="sr-muted" style={{ fontSize: 13 }}>{c.body}</p>
                        {c.cta && <span className="sr-badge sr-badge-gold">{c.cta}</span>}
                      </>
                    )}
                    {sc && (
                      <div className="sr-muted" style={{ fontSize: 11, marginTop: 8 }} data-testid={`score-${c.id}`}>
                        Clarity {sc.clarity} · Offer {sc.offer_strength} · Score {sc.score}
                      </div>
                    )}
                    {isSuggested && sc?.insight && (
                      <div style={{ fontSize: 11, marginTop: 4, color: "#B8791B" }} data-testid={`insight-${c.id}`}>
                        <Sparkles size={10} style={{ verticalAlign: "-1px" }} /> {sc.insight}
                      </div>
                    )}
                    <button className={`sr-btn sr-btn-sm ${c.winner ? "" : "sr-btn-primary"}`} style={{ width: "100%", marginTop: 10 }}
                      onClick={() => chooseWinner(c.id)} disabled={c.winner} data-testid={`pick-winner-${c.id}`}>
                      {c.winner ? <><Trophy size={13} /> Winner</> : "Pick this one"}
                    </button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ width: "100%", marginTop: 6 }}
                      onClick={() => launchFromCompare(c)} data-testid={`compare-launch-${c.id}`}>
                      <Megaphone size={13} /> Launch this
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {presetModal && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setPresetModal(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="preset-modal">
          <div className="sr-card" style={{ maxWidth: 440, width: "100%" }}>
            <h3 style={{ fontSize: 18, marginBottom: 4 }}><Plus size={16} style={{ verticalAlign: "-2px", color: "var(--blue)" }} /> Save a refine prompt</h3>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 0 }}>Save a favorite instruction as a one-tap chip you can reuse on any copy — e.g. "Add a limited-time offer".</p>
            <input className="sr-input" value={presetModal.text} onChange={(e) => setPresetModal((m) => ({ ...m, text: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && savePreset()} placeholder="e.g. Make it more local and friendly" data-testid="preset-input" style={{ width: "100%", marginTop: 8 }} maxLength={120} autoFocus />
            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setPresetModal(null)} data-testid="preset-cancel">Cancel</button>
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={savePreset} disabled={presetModal.busy || !presetModal.text.trim()} data-testid="preset-save">
                {presetModal.busy ? <Loader2 size={14} className="sr-spin" /> : <Plus size={14} />} Save preset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
